import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import firebase from 'firebase/app';
import 'firebase/firestore';

const MaintenancePage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      // Save email to Firestore for notifications
      await firebase.firestore().collection('maintenance_notifications').add({
        email: email.toLowerCase().trim(),
        subscribedAt: new Date().toISOString(),
        notified: false
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error saving email:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Under Maintenance | PreInterview</title>
        <meta
          name="description"
          content="We're currently performing scheduled maintenance. We'll be back soon!"
        />
      </Helmet>
      <div className="maintenance-page">
        <div className="maintenance-container">
          {/* Animated gear icon */}
          <div className="maintenance-icon">
            <svg
              className="gear-icon"
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="currentColor"
                d="M97.6,55.7V44.3l-12.4-2.4c-0.9-3.2-2.1-6.2-3.8-9.1l7.4-10.3l-8-8l-10.3,7.4c-2.9-1.6-5.9-2.9-9.1-3.8L59.1,5.7h-8
                c-3.3,0-6.5,0.3-9.6,0.9L38.3,2.4l-8,8l5.6,9.8c-2.3,2.1-4.4,4.5-6.2,7.1l-11.3-3.2l-5.7,9.8l9.4,7.5c-0.8,3-1.3,6.1-1.3,9.3v2.6
                l-11.5,4.2l2.4,11.1l12.1-0.8c1.3,3,2.9,5.8,4.8,8.4l-5.2,11.1l9.8,5.7l8.3-9c3.2,1.1,6.5,1.8,10,2.1l4.4,11.5h11.3l4.4-11.5
                c3.5-0.3,6.8-1,10-2.1l8.3,9l9.8-5.7l-5.2-11.1c1.9-2.6,3.5-5.4,4.8-8.4l12.1,0.8l2.4-11.1L97.6,55.7z M50,70
                c-11,0-20-9-20-20s9-20,20-20s20,9,20,20S61,70,50,70z"
              />
            </svg>
          </div>

          {/* Main content */}
          <h1 className="maintenance-title">We're Under Maintenance</h1>
          <p className="maintenance-subtitle">
            We're working hard to improve your experience. Our site is currently
            undergoing scheduled maintenance.
          </p>

          {/* Status card */}
          <div className="status-card">
            <div className="status-header">
              <span className="status-dot"></span>
              <span>Maintenance in Progress</span>
            </div>
            <p className="status-message">
              We appreciate your patience. Our team is working to bring the
              platform back online as soon as possible.
            </p>
          </div>

          {/* Email notification signup */}
          <div className="notification-section">
            <h2 className="notification-title">Get Notified When We're Back</h2>
            <p className="notification-subtitle">
              Enter your email and we'll let you know as soon as we're back online.
            </p>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="notification-form">
                <div className="input-wrapper">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="email-input"
                    disabled={isSubmitting}
                  />
                  <button
                    type="submit"
                    className="notify-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="loading-spinner"></span>
                    ) : (
                      'Notify Me'
                    )}
                  </button>
                </div>
                {error && <p className="error-message">{error}</p>}
              </form>
            ) : (
              <div className="success-message">
                <svg className="success-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <p>Thanks! We'll email you when we're back.</p>
              </div>
            )}
          </div>

          {/* Contact info */}
          <div className="contact-section">
            <p>Need immediate assistance?</p>
            <a href="mailto:support@prepbestie.com" className="contact-link">
              support@prepbestie.com
            </a>
          </div>

          {/* Decorative elements */}
          <div className="floating-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
          </div>
        </div>

        <style>{`
          .maintenance-page {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%);
            color: #ffffff;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 2rem;
            position: relative;
            overflow: hidden;
          }

          .maintenance-container {
            max-width: 600px;
            text-align: center;
            position: relative;
            z-index: 10;
          }

          .maintenance-icon {
            margin-bottom: 2rem;
          }

          .gear-icon {
            width: 100px;
            height: 100px;
            color: #6366f1;
            animation: rotate 8s linear infinite;
          }

          @keyframes rotate {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }

          .maintenance-title {
            font-size: 2.5rem;
            font-weight: 700;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #818cf8 0%, #c084fc 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .maintenance-subtitle {
            font-size: 1.125rem;
            color: #a5b4fc;
            line-height: 1.7;
            margin-bottom: 2rem;
          }

          .status-card {
            background: rgba(99, 102, 241, 0.1);
            border: 1px solid rgba(99, 102, 241, 0.3);
            border-radius: 16px;
            padding: 1.5rem;
            margin-bottom: 2rem;
            backdrop-filter: blur(10px);
          }

          .status-header {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.75rem;
            font-weight: 600;
            font-size: 1rem;
            margin-bottom: 0.75rem;
            color: #818cf8;
          }

          .status-dot {
            width: 10px;
            height: 10px;
            background: #f59e0b;
            border-radius: 50%;
            animation: pulse 2s ease-in-out infinite;
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.5;
              transform: scale(1.2);
            }
          }

          .status-message {
            color: #c7d2fe;
            font-size: 0.95rem;
            line-height: 1.6;
            margin: 0;
          }

          /* Email notification section */
          .notification-section {
            background: rgba(16, 185, 129, 0.1);
            border: 1px solid rgba(16, 185, 129, 0.3);
            border-radius: 16px;
            padding: 1.75rem;
            margin-bottom: 2rem;
            backdrop-filter: blur(10px);
          }

          .notification-title {
            font-size: 1.25rem;
            font-weight: 600;
            color: #34d399;
            margin-bottom: 0.5rem;
          }

          .notification-subtitle {
            color: #a7f3d0;
            font-size: 0.9rem;
            margin-bottom: 1.25rem;
          }

          .notification-form {
            width: 100%;
          }

          .input-wrapper {
            display: flex;
            gap: 0.75rem;
            flex-wrap: wrap;
            justify-content: center;
          }

          .email-input {
            flex: 1;
            min-width: 200px;
            padding: 0.875rem 1rem;
            border-radius: 10px;
            border: 2px solid rgba(52, 211, 153, 0.3);
            background: rgba(0, 0, 0, 0.3);
            color: #ffffff;
            font-size: 1rem;
            outline: none;
            transition: all 0.3s ease;
          }

          .email-input::placeholder {
            color: #6b7280;
          }

          .email-input:focus {
            border-color: #34d399;
            box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.2);
          }

          .email-input:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .notify-button {
            padding: 0.875rem 1.5rem;
            border-radius: 10px;
            border: none;
            background: linear-gradient(135deg, #10b981, #34d399);
            color: #ffffff;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            min-width: 120px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .notify-button:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 4px 20px rgba(16, 185, 129, 0.4);
          }

          .notify-button:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }

          .loading-spinner {
            width: 20px;
            height: 20px;
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-top-color: #ffffff;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }

          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }

          .error-message {
            color: #f87171;
            font-size: 0.85rem;
            margin-top: 0.75rem;
          }

          .success-message {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.75rem;
            color: #34d399;
            font-weight: 500;
          }

          .success-icon {
            width: 24px;
            height: 24px;
            color: #34d399;
          }

          .contact-section {
            margin-top: 1.5rem;
          }

          .contact-section p {
            color: #94a3b8;
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
          }

          .contact-link {
            color: #818cf8;
            text-decoration: none;
            font-weight: 500;
            transition: color 0.3s ease;
          }

          .contact-link:hover {
            color: #a5b4fc;
            text-decoration: underline;
          }

          /* Floating shapes for visual interest */
          .floating-shapes {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            overflow: hidden;
            z-index: 1;
          }

          .shape {
            position: absolute;
            border-radius: 50%;
            opacity: 0.1;
          }

          .shape-1 {
            width: 400px;
            height: 400px;
            background: linear-gradient(135deg, #6366f1, #8b5cf6);
            top: -100px;
            right: -100px;
            animation: float 20s ease-in-out infinite;
          }

          .shape-2 {
            width: 300px;
            height: 300px;
            background: linear-gradient(135deg, #8b5cf6, #c084fc);
            bottom: -50px;
            left: -50px;
            animation: float 15s ease-in-out infinite reverse;
          }

          .shape-3 {
            width: 200px;
            height: 200px;
            background: linear-gradient(135deg, #c084fc, #f472b6);
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            animation: float 25s ease-in-out infinite;
          }

          @keyframes float {
            0%, 100% {
              transform: translate(0, 0) scale(1);
            }
            25% {
              transform: translate(20px, -20px) scale(1.05);
            }
            50% {
              transform: translate(-10px, 30px) scale(0.95);
            }
            75% {
              transform: translate(-30px, -10px) scale(1.02);
            }
          }

          @media (max-width: 640px) {
            .maintenance-title {
              font-size: 1.75rem;
            }

            .maintenance-subtitle {
              font-size: 1rem;
            }

            .gear-icon {
              width: 80px;
              height: 80px;
            }

            .status-card,
            .notification-section {
              padding: 1.25rem;
            }

            .input-wrapper {
              flex-direction: column;
            }

            .email-input {
              min-width: 100%;
            }

            .notify-button {
              width: 100%;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default MaintenancePage;

